import { useAuth0 } from "@auth0/auth0-react";
import axios, {
  type Axios,
  type AxiosResponse,
  type CreateAxiosDefaults,
} from "axios";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

let hostname = "";
// We set this so that it's only run on the client side.
if (typeof window !== "undefined") {
  hostname = window?.location?.hostname;
}

enum Backend {
  Local,
  Staging,
  Demo,
  Production,
}

const PRODUCTION_HOSTNAMES = ["app.wieldy.ai"];

function getBackend(host: string = hostname): Backend {
  const normalized = host.toLowerCase();

  if (normalized.includes("demo")) {
    return Backend.Demo;
  }

  if (normalized.includes("staging")) {
    return Backend.Staging;
  }

  if (PRODUCTION_HOSTNAMES.includes(normalized)) {
    return Backend.Production;
  }

  return Backend.Local;
}

function getBackendAPI(): string {
  switch (getBackend()) {
    case Backend.Local:
      return "http://localhost:8000/api/external/";
    case Backend.Staging:
      return "https://staging.api.wieldy.ai/api/external/";
    case Backend.Demo:
      return "https://demo.api.wieldy.ai/api/external/";
    case Backend.Production:
      return "https://api.wieldy.ai/api/external/";
    default:
      throw new Error("Backend should be local, staging, demo, or production");
  }
}

function getAdminAppUrl(): string {
  switch (getBackend()) {
    case Backend.Local:
      return "http://localhost:3001";
    case Backend.Staging:
    case Backend.Demo:
      return "https://staging.admin.wieldy.ai";
    case Backend.Production:
      return "https://admin.wieldy.ai";
    default:
      throw new Error("Backend should be local, staging, demo, or production");
  }
}

function getWieldyOrgId(): string {
  switch (getBackend()) {
    case Backend.Local:
      return "org_TUdeyVSsLSVBw5P2";
    case Backend.Staging:
      return "org_uOHLmiXsj6v9yu5M";
    case Backend.Demo:
      return "org_uOHLmiXsj6v9yu5M";
    case Backend.Production:
      return "org_fpufEjVSy6303fBL";
    default:
      throw new Error("Backend should be local, staging, demo, or production");
  }
}

function getGoogleOrgId(): string {
  switch (getBackend()) {
    case Backend.Local:
      return "org_txH3uVXTHQRVRMLa";
    case Backend.Staging:
      return "org_yzQWpwZZH2k1P2Gb";
    case Backend.Demo:
      return "org_yzQWpwZZH2k1P2Gb";
    case Backend.Production:
      return "org_PH2kIYfk6hHVLOg3";
    default:
      throw new Error("Backend should be local, staging, demo, or production");
  }
}

const FINIX_ENVIRONMENT = {
  [Backend.Local]: "sandbox",
  [Backend.Staging]: "sandbox",
  [Backend.Demo]: "sandbox",
  [Backend.Production]: "live",
}[getBackend()];

const FINIX_APPLICATION_ID = {
  [Backend.Local]: "APg9hedUxuBokSwDswxTxtaE",
  [Backend.Staging]: "APg9hedUxuBokSwDswxTxtaE",
  [Backend.Demo]: "APg9hedUxuBokSwDswxTxtaE",
  [Backend.Production]: "AP8LuL1XzLR661owkPq4whE2",
}[getBackend()];

const AUTH = {
  [Backend.Local]: false,
  [Backend.Staging]: true,
  [Backend.Demo]: true,
  [Backend.Production]: true,
};

const USE_AUTH = AUTH[getBackend()];

const useApi = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding navigate would change when this runs
  const getClient = useCallback(async (): Promise<Axios> => {
    const options: CreateAxiosDefaults = {
      baseURL: getBackendAPI(),
    };
    if (USE_AUTH) {
      const accessToken = await getAccessTokenSilently();
      options.headers = {
        Authorization: `Bearer ${accessToken}`,
      };
    }
    const instance = axios.create(options);
    instance.interceptors.response.use((response: AxiosResponse) => {
      const redirectPath: string | undefined | null =
        response.headers["x-redirect-path"];
      if (redirectPath) {
        navigate(redirectPath);
      }
      return response;
    });
    return instance;
  }, [getAccessTokenSilently]);

  return useMemo(() => ({ getClient }), [getClient]);
};

export default useApi;
export {
  Backend,
  FINIX_APPLICATION_ID,
  FINIX_ENVIRONMENT,
  getAdminAppUrl,
  getBackend,
  getBackendAPI,
  getGoogleOrgId,
  getWieldyOrgId,
  USE_AUTH,
};
